import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import Grid from '@mui/material/Grid';
import * as sdkTypes from '@vansite/ts-sharetribe-flex-sdk/types';
import Decimal from 'decimal.js';
import moment from 'moment';
import { string } from 'prop-types';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useIntl } from 'react-intl';
import { shallowEqual, useDispatch } from 'react-redux';
import { useLocation, useNavigate } from 'react-router';

import { FirebaseShareEvent } from '../../analytics/firebase';
import BoxSeparator from '../../components/BoxSeperator/BoxSeparator';
import BottomNavigationBar from '../../components/Layouts/BottomNavigationBar/BottomNavigationBar';
import Footer from '../../components/Layouts/Footer/Footer';
import LayoutSingleColumn from '../../components/Layouts/LayoutSingleColumn';
import LayoutWrapperBottombar from '../../components/Layouts/LayoutWrapperBottombar/LayoutWrapperBottombar';
import LayoutWrapperFooter from '../../components/Layouts/LayoutWrapperFooter/LayoutWrapperFooter';
import LayoutWrapperMain from '../../components/Layouts/LayoutWrapperMain';
import LayoutWrapperTopbar from '../../components/Layouts/LayoutWrapperTopbar/LayoutWrapperTopbar';
import TopBar from '../../components/Layouts/TopBar/TopBar';
import NamedRedirect from '../../components/NamedRedirect/NamedRedirect';
import Page from '../../components/Page/Page';
import config from '../../config';
import { getMarketplaceEntities, getTranslationEntities } from '../../ducks/marketplaceData.duck';
import { initializePaymentData } from '../../ducks/stripe.duck';
import routeConfiguration from '../../routeConfiguration';
import {
  hasOpenBookingLimit,
  isFlexPass,
  PASS_ANNUAL_PASS_VOUCHER,
  PASS_ANNUAL_TWENTY_FIVE_PASS_VOUCHER,
  PASS_FLEX_PASS,
} from '../../util/annualPass';
import { isCampingGroundListing, userDisplayNameAsString } from '../../util/data';
import useAppSelector from '../../util/hooks/useAppSelector';
import usePass from '../../util/hooks/usePass';
import usePriceData from '../../util/hooks/usePriceData';
import { isCordova } from '../../util/mobile/deviceDetection';
import { openDeviceShareDialog } from '../../util/mobile/mobile';
import { FormattedMessage } from '../../util/reactIntl';
import { richText } from '../../util/richText';
import {
  createResourceLocatorString,
  findRouteByRouteName,
  locationToHref,
} from '../../util/routes';
import { findOptionsForSelectFilter } from '../../util/search';
import { LISTING_STATE_CLOSED, LISTING_STATE_PENDING_APPROVAL } from '../../util/types';
import {
  createSlug,
  LISTING_PAGE_DRAFT_VARIANT,
  LISTING_PAGE_PARAM_TYPE_DRAFT,
  LISTING_PAGE_PARAM_TYPE_EDIT,
  LISTING_PAGE_PENDING_APPROVAL_VARIANT,
} from '../../util/urlHelpers';
import { persistBookingForLogin } from '../CheckoutPage/CheckoutPageSessionHelpers';
import ActionBarMaybe from './ActionBarMaybe';
import {
  fetchNearbyListings,
  fetchTimeSlots,
  fetchTransactionLineItems,
  NEARBY_MIN,
} from './ListingPage.duck';
import useCurrentListing from './useCurrentListing';
import ListingPageError from './ListingPage.error';
import ListingPageLoading from './ListingPage.loading';
import ListingSection from './ListingSection';
import ListingSectionItem from './ListingSectionItem';
import SectionActivitiesMaybe from './SectionActivities/SectionActivitiesMaybe';
import SectionAmenitiesMaybe from './SectionAmenities/SectionAmenitiesMaybe';
import SectionAnnualPassSavingsMaybe from './SectionAnnualPassSavingsMaybe';
import SectionCancellationPolicyMaybe from './SectionCancellationPolicyMaybe';
import SectionCustomLineItemsDescriptionMaybe from './SectionCustomLineItemsDescriptionMaybe';
import SectionDescriptionMaybe from './SectionDescription/SectionDescriptionMaybe';
import SectionHeading from './SectionHeading/SectionHeading';
import SectionHostMaybe from './SectionHostMaybe';
import SectionImages from './SectionImages/SectionImages';
import SectionMapMaybe from './SectionMapMaybe';
import SectionMoreInformationMaybe from './SectionMoreInformationMaybe';
import SectionNearbyListingsMaybe from './SectionNearbyListingsMaybe';
import SectionReviews from './SectionReviews';
import SectionRulesMaybe from './SectionRulesMaybe';
import SectionSurroundingsMaybe from './SectionSurroundings/SectionSurroundingsMaybe';
import SectionTimingsMaybe from './SectionTimingsMaybe';
import SectionVehiclesMaybe from './SectionVehicles/SectionVehiclesMaybe';

const MIN_LENGTH_FOR_LONG_WORDS_IN_TITLE = 16;

const LONG_WORD_CLASS = 'ListingPage_longWord';

const richTitleSx = {
  [`& .${LONG_WORD_CLASS}`]: {
    // 'break-all' is the fallback option; 'break-word' is used if available.
    // Emotion emits both declarations from the array (later one wins where supported).
    wordBreak: ['break-all', 'break-word'],
    hyphens: 'auto',
  },
};

const ANNUAL_PASS_BOOKING_LIMIT = config.annualPass.bookingLimit;
const ANNUAL_PASS_SIMULTANEOUSLY_BOOKING_LIMIT = config.annualPass.simultaneouslyBookingLimit;

const filterConfig = config.custom.filters;
const unitType = config.bookingUnitType;

const PRODUCTION_ENV = process.env.REACT_APP_ENV === 'production';

const { UUID, Money } = sdkTypes;

const priceRange = (price) => {
  if (price <= 1000) {
    return '€';
  } else if (price > 1000 && price <= 2000) {
    return '€€';
  } else if (price > 2000 && price <= 3000) {
    return '€€€';
  } else {
    return '€€€€';
  }
};

/**
 * This component is the root component for the ListingPage. It fetches all the
 * data required for the page and passes it to the sub-components.
 * @param {object} rawParams
 * @param {string} rawParams.id - listing UUID string
 * @param {'pending-approval' | 'draft'} [rawParams.variant] - listing variant
 * @param {string} [rawParams.slug] - listing slug
 * @returns {React.ReactNode}
 * @class
 */

export const ListingPage = ({ params: rawParams }) => {
  const listingId = useMemo(() => new UUID(rawParams.id), [rawParams.id]);

  const entities = useAppSelector((state) => state.marketplaceData.entities);

  const entitiesRef = useRef(entities);
  useEffect(() => {
    entitiesRef.current = entities;
  }, [entities]);

  // handleSubmit reads the listing through entitiesRef to keep a stable
  // callback identity without depending on entities.
  const getListing = (id, entities) => {
    const ref = { id, type: 'listing' };
    const listings = getMarketplaceEntities(entities, [ref]);
    return listings.length === 1 ? listings[0] : null;
  };

  const isPendingApprovalVariant = rawParams.variant === LISTING_PAGE_PENDING_APPROVAL_VARIANT;
  const isDraftVariant = rawParams.variant === LISTING_PAGE_DRAFT_VARIANT;

  /** @typedef import('@vansite/ts-sharetribe-flex-sdk').Listing */
  const currentListing = useCurrentListing(listingId, isPendingApprovalVariant || isDraftVariant);

  const [openShareModal, setOpenShareModal] = useState(false);
  const [nextPeriodDialogOpen, setNextPeriodDialogOpen] = useState(false);
  const [pendingBookingValues, setPendingBookingValues] = useState(null);
  const skipPassRef = useRef(false);

  const translationEntities = useAppSelector((state) => state.marketplaceData.translationEntities);

  const getTranslation = useCallback(
    (id, type, lang) => getTranslationEntities(translationEntities, { id, type, lang }),
    [translationEntities],
  );

  const {
    fetchLineItemsError,
    fetchLineItemsInProgress,
    fetchTimeSlotsInProgress,
    fetchTimeSlotsError,
    lineItems,
    nearbyListingIds,
    fetchNearbyInProgress,
    reviews,
    showListingError,
    timeSlots,
    toManyTransactionsWithProvider,
    fetchReviewsError,
  } = useAppSelector(
    (state) => ({
      fetchLineItemsError: state.ListingPage.fetchLineItemsError,
      fetchLineItemsInProgress: state.ListingPage.fetchLineItemsInProgress,
      fetchTimeSlotsInProgress: state.ListingPage.fetchTimeSlotsInProgress,
      fetchTimeSlotsError: state.ListingPage.fetchTimeSlotsError,
      lineItems: state.ListingPage.lineItems,
      nearbyListingIds: state.ListingPage.nearbyListingIds,
      fetchNearbyInProgress: state.ListingPage.fetchNearbyInProgress,
      reviews: state.ListingPage.reviews,
      showListingError: state.ListingPage.showListingError,
      timeSlots: state.ListingPage.timeSlots,
      toManyTransactionsWithProvider: state.ListingPage.toManyTransactionsWithProvider,
      fetchReviewsError: state.ListingPage.fetchReviewsError,
    }),
    shallowEqual
  );

  const nearbyListings = useMemo(
    () =>
      getMarketplaceEntities(
        entities,
        nearbyListingIds.map((id) => ({ id, type: 'listing' })),
      ),
    [entities, nearbyListingIds],
  );

  const voucher = useAppSelector((state) => state.voucherify.voucher);
  const voucherValidationInProgress = useAppSelector(
    (state) => state.voucherify.voucherValidationInProgress
  );
  const voucherValidationError = useAppSelector((state) => state.voucherify.voucherValidationError);
  const currentUser = useAppSelector((state) => state.user.currentUser);
  const currentUserOpenOrdersCount = useAppSelector(
    (state) => state.user.currentUserOpenOrdersCount
  );

  const dispatch = useDispatch();
  const [passIsValid, pass] = usePass();

  const onFetchTransactionLineItems = useCallback(
    (params) => {
      dispatch(fetchTransactionLineItems(params));
    },
    [dispatch]
  );
  const onFetchTimeSlots = useCallback(
    (page) => dispatch(fetchTimeSlots(listingId, page)),
    [dispatch, listingId]
  );

  const intl = useIntl();
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    onFetchTimeSlots(0);
  }, []);

  const sectionMoreInformationRef = useRef(null); // used for the bookingpanel to stop scrolling here
  const reviewsRef = useRef(null);

  const { defaultLocale, translations } = getTranslation(listingId.uuid, 'listing', intl.locale);
  const isTranslated = !!defaultLocale && defaultLocale !== intl.locale;

  const {
    description = '',
    geolocation = null,
    title = '',
    publicData,
    price,
    metadata,
  } = currentListing.attributes;

  // Nearby listings are fetched on the client, not during SSR: the query needs
  // the listing's geolocation (only known after it loads) and is below the fold,
  // and the client router does not re-run loadData after hydration — so SSR
  // can't fill this in. Re-runs when navigating to a different listing.
  const currentListingId = currentListing.id?.uuid;
  useEffect(() => {
    if (currentListingId && geolocation) {
      dispatch(fetchNearbyListings(currentListing));
    }
    // currentListing is intentionally excluded from deps; we key on its id and
    // coordinates so the fetch fires once per listing, not on every re-render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, currentListingId, geolocation?.lat, geolocation?.lng]);

  const listingSlug = rawParams.slug || createSlug(currentListing.attributes.title || '');
  const params = useMemo(() => ({ slug: listingSlug, ...rawParams }), [listingSlug, rawParams]);

  const annualPassCode = pass?.code;
  const annualPassSubscriptionId = pass?.subscriptionId;
  const handleSubmit = useCallback(
    async (values) => {
      const listingId = new UUID(params.id);
      const listing = getListing(listingId, entitiesRef.current);

      const { bookingDates, bookingData } = values;

      bookingData.voucher = voucher?.valid ? voucher : null;

      if ((annualPassCode || annualPassSubscriptionId) && passIsValid && !skipPassRef.current) {
        bookingData.annualPassVoucher = {
          ...pass,
          type: pass?.twentyFivePass
            ? PASS_ANNUAL_TWENTY_FIVE_PASS_VOUCHER
            : isFlexPass(pass)
              ? PASS_FLEX_PASS
              : PASS_ANNUAL_PASS_VOUCHER,
        };
      }
      skipPassRef.current = false;

      const initialValues = {
        listing,
        bookingData,
        bookingDates,
        confirmPaymentError: null,
      };

      const routes = routeConfiguration();
      // Customize checkout page state with current listing and selected bookingDates
      const { loadDuck } = findRouteByRouteName('CheckoutPage', routes);

      // Keep in lock-step with toManyOpenOrdersAsAnnualPassUser in
      // BookingPanel: 25Pass and FlexPass have no open-booking limit. The
      // panel shows the warning and disables the button; this is only the
      // last-resort guard for the same condition.
      if (
        PRODUCTION_ENV &&
        currentUser?.id &&
        passIsValid &&
        hasOpenBookingLimit(pass) &&
        currentUserOpenOrdersCount >= ANNUAL_PASS_SIMULTANEOUSLY_BOOKING_LIMIT
      ) {
        return;
      }

      // @ts-ignore
      const { setInitialValues } = await loadDuck();
      dispatch(setInitialValues(initialValues));

      // Persist the booking for logged-out users so it survives the login
      // round-trip the auth guard forces (incl. a full reload on the login
      // page); CheckoutPage restores it from sessionStorage after sign-in.
      persistBookingForLogin(currentUser, initialValues);

      // Clear previous Stripe errors from store if there is any
      dispatch(initializePaymentData());

      // Redirect to CheckoutPage
      navigate(
        createResourceLocatorString(
          'CheckoutPage',
          routes,
          {
            id: listing.id.uuid,
            slug: createSlug(listing.attributes.title),
          },
          {},
          intl.locale
        )
      );
    },
    [
      params.id,
      entities,
      voucher,
      annualPassCode,
      passIsValid,
      pass,
      currentUser,
      currentUserOpenOrdersCount,
      dispatch,
      navigate,
      intl.locale,
    ]
  );

  const onShareListing = useCallback(() => {
    const listingId = new UUID(rawParams.id);
    if (isCordova()) {
      openDeviceShareDialog(
        {
          subject: currentListing.attributes.title,
          url: locationToHref(location, config.canonicalRootURL),
          message: translations.description || currentListing.attributes.description,
        },
        listingId,
        currentUser
      );
    } else if (navigator.share) {
      navigator
        .share({
          title: currentListing.attributes.title,
          url: window.location.href,
          text: translations.description || currentListing.attributes.description,
        })
        .then(() => {
          FirebaseShareEvent.withItemId(listingId).withMethod('navigator').logEvent();
        })
        .catch(null);
    } else {
      setOpenShareModal(!openShareModal);
    }
  }, [currentListing, currentUser, location, openShareModal, rawParams.id, translations]);

  const listingType = isDraftVariant ? LISTING_PAGE_PARAM_TYPE_DRAFT : LISTING_PAGE_PARAM_TYPE_EDIT;
  const listingTab = isDraftVariant ? 'photos' : 'description';

  const isApproved =
    currentListing.id && currentListing.attributes.state !== LISTING_STATE_PENDING_APPROVAL;

  const pendingIsApproved = isPendingApprovalVariant && isApproved;

  //Booking Limit
  const bookingLimitOptions = findOptionsForSelectFilter('bookingLimit', filterConfig);
  const bookingMinimumOption = findOptionsForSelectFilter('bookingMinimum', filterConfig);
  // bookingLimit null mean there is no booking limit
  const bookingLimitValue = useMemo(() => {
    const bookingLimit = publicData && publicData.bookingLimit ? publicData.bookingLimit : null;
    const limit = bookingLimit
      ? Array.isArray(bookingLimit)
        ? !bookingLimit.includes('limit_bigger_4')
          ? parseInt(bookingLimit[bookingLimit.length - 1].replace('limit_', ''))
          : null
        : bookingLimit.includes('bigger')
          ? null
          : parseInt(bookingLimit.replace('limit_', ''))
      : null;

    return !passIsValid ? limit : pass && pass.twentyFivePass ? limit : ANNUAL_PASS_BOOKING_LIMIT;
  }, [publicData, passIsValid]);

  const bookingMinimumValue = useMemo(() => {
    const bookingMinimum =
      publicData && publicData.bookingMinimum ? publicData.bookingMinimum : 'min_1';
    const limit = bookingMinimum.replace('min_', '');
    return parseInt(limit);
  });

  // If a /pending-approval URL is shared, the UI requires
  // authentication and attempts to fetch the listing from own
  // listings. This will fail with 403 Forbidden if the author is
  // another user. We use this information to try to fetch the
  // public listing.
  const pendingOtherUsersListing =
    (isPendingApprovalVariant || isDraftVariant) &&
    showListingError &&
    showListingError.status === 403;
  const shouldShowPublicListingPage = pendingIsApproved || pendingOtherUsersListing;

  const authorAvailable = currentListing && currentListing.author;
  const userAndListingAuthorAvailable = !!(currentUser && authorAvailable);
  const isOwnListing =
    userAndListingAuthorAvailable && currentListing.author.id.uuid === currentUser.id.uuid;

  const currentAuthor = authorAvailable ? currentListing.author : null;

  const handleBookingSubmit = useCallback(
    (values) => {
      const isCurrentlyClosed = currentListing.attributes.state === LISTING_STATE_CLOSED;
      if (isOwnListing || isCurrentlyClosed) {
        window.scrollTo(0, 0);
        return;
      }

      // Check if booking falls outside pass validity period
      if (passIsValid && pass?.validUntil && !pass.nextPeriodPrepaidCoupon) {
        const bookingStart = values.bookingDates?.bookingStart;
        const bookingEnd = values.bookingDates?.bookingEnd;
        // Compare calendar days, not timestamps: the picker returns
        // local-midnight dates (converted to UTC midnight only later in
        // CheckoutPage via dateFromLocalToAPI), while pass dates are UTC and
        // may carry a time of day (e.g. rental pickup time).
        const bookingStartDay = bookingStart && moment(bookingStart).format('YYYY-MM-DD');
        const bookingEndDay = bookingEnd && moment(bookingEnd).format('YYYY-MM-DD');
        const endsAfterValidity =
          bookingEndDay && bookingEndDay > moment.utc(pass.validUntil).format('YYYY-MM-DD');
        // FlexPass has a start date; booking before it is also outside the pass.
        const startsBeforeFlexPass =
          isFlexPass(pass) &&
          pass.rentalStartDate &&
          bookingStartDay &&
          bookingStartDay < moment.utc(pass.rentalStartDate).format('YYYY-MM-DD');

        if (endsAfterValidity || startsBeforeFlexPass) {
          setPendingBookingValues(values);
          setNextPeriodDialogOpen(true);
          return;
        }
      }

      handleSubmit(values);
    },
    [currentListing, handleSubmit, isOwnListing, pass, passIsValid]
  );

  const handleContinueWithoutPass = useCallback(() => {
    setNextPeriodDialogOpen(false);
    if (pendingBookingValues) {
      skipPassRef.current = true;
      handleSubmit(pendingBookingValues);
    }
  }, [pendingBookingValues, handleSubmit]);

  const handleGoToPassSettings = useCallback(() => {
    setNextPeriodDialogOpen(false);
    const routes = routeConfiguration();
    navigate(createResourceLocatorString('PassSettingsPage', routes, {}, {}, intl.locale));
  }, [navigate, intl.locale]);

  const richTitle = (
    <Box component="span" sx={richTitleSx}>
      {richText(title, {
        longWordMinLength: MIN_LENGTH_FOR_LONG_WORDS_IN_TITLE,
        longWordClass: LONG_WORD_CLASS,
      })}
    </Box>
  );

  const bookingTitle = (
    <FormattedMessage id="ListingPage.bookingTitle" values={{ title: richTitle }} />
  );

  const topbar = <TopBar />;

  const listingHasSpecialPrice = metadata?.annualPassPrice;
  const priceAmount =
    !!listingHasSpecialPrice && passIsValid
      ? listingHasSpecialPrice.amount
      : currentListing?.attributes?.price?.amount || 0;
  const listingIsAbovePriceThreshold = priceAmount >= config.annualPass.lightPriceThreshold;
  const userHasPlusAnnualPass =
    pass &&
    !pass.twentyFivePass &&
    (pass.included === undefined ||
      pass.included?.plus === undefined ||
      pass.included?.plus === true);
  const showReducedPrice =
    !userHasPlusAnnualPass && passIsValid && listingIsAbovePriceThreshold
      ? !userHasPlusAnnualPass
      : null;
  const showTwentyFivePassPrice = passIsValid && pass?.twentyFivePass;

  const priceToUse = new Money(priceAmount, config.currency);
  const { formattedPrice, priceTitle } = usePriceData(priceToUse || new Money(0, config.currency));

  const { formattedPrice: reducedFormattedPrice, priceTitle: reducedPriceTitle } = usePriceData(
    price
      ? new Money(
          new Decimal(pass?.twentyFivePass ? price.amount : price.amount / 2).round().toNumber(),
          config.currency
        )
      : new Money(0, config.currency),
    price ? (pass?.twentyFivePass ? Math.round(price.amount * 0.25) : 0) : 0
  );

  if (shouldShowPublicListingPage) {
    return <NamedRedirect name="ListingPage" params={params} search={location.search} />;
  }

  if (showListingError && showListingError.status === 404) {
    // 404 listing not found
    return <NamedRedirect name="NotFoundPage" />;
  } else if (showListingError) {
    // Other error in fetching listing
    return <ListingPageError topbar={topbar} />;
  } else if (!currentListing.id) {
    // Still loading the listing
    return <ListingPageLoading topbar={topbar} />;
  }

  // When user is banned or deleted the listing is also deleted.
  // Because listing can be never showed with banned or deleted user we don't have to provide
  // banned or deleted display names for the function
  const authorDisplayName = userDisplayNameAsString(currentAuthor, '');

  const listingImages = (listing, variantName) =>
    (listing.images || [])
      .map((image) => {
        const variants = image.attributes.variants;
        const variant = variants ? variants[variantName] : null;

        // deprecated
        // for backwards combatility only
        const sizes = image.attributes.sizes;
        const size = sizes ? sizes.find((i) => i.name === variantName) : null;

        return variant || size;
      })
      .filter((variant) => variant != null);

  const facebookImages = listingImages(currentListing, 'facebook');
  const twitterImages = listingImages(currentListing, 'twitter');
  const schemaImages = facebookImages.map((img) => img.url);
  const siteTitle = config.siteTitle;
  const { locationPlace } = publicData;
  const schemaTitle = intl.formatMessage(
    { id: 'ListingPage.schemaTitle' },
    {
      title: title,
      location: locationPlace,
      siteTitle,
    }
  );

  const ratingCount = reviews.length;
  const totalScore = reviews.reduce((acc, review) => acc + review.attributes.rating, 0);
  const ratingValue = Math.round(ratingCount ? totalScore / ratingCount : 0);
  const bestRating = reviews.reduce((acc, review) => Math.max(acc, review.attributes.rating), 0);
  const wortRating = reviews.reduce((acc, review) => Math.min(acc, review.attributes.rating), 5);
  const schemaReviews = reviews.map((review) => {
    const authorName = review.author?.attributes?.profile?.displayName;
    return {
      '@type': 'Review',
      ...(authorName
        ? {
            author: {
              '@type': 'Person',
              name: review.author.attributes.profile.displayName,
            },
          }
        : {
            '@type': 'Person',
            name: 'Unknown User',
          }),
      datePublished: review.attributes.createdAt,
      reviewBody: review.attributes.content,
      reviewRating: {
        '@type': 'Rating',
        ratingValue: review.attributes.rating,
      },
    };
  });

  const editParams = {
    id: currentListing.id.uuid,
    slug: listingSlug,
    type: listingType,
    tab: listingTab,
  };
  // Action bar is wrapped with a div that prevents the click events
  // to the parent that would otherwise open the image carousel
  const actionBar = currentListing.id ? (
    <Box onClick={(e) => e.stopPropagation()}>
      <ActionBarMaybe
        editParams={editParams}
        isOwnListing={isOwnListing}
        listing={currentListing}
      />
    </Box>
  ) : null;

  // Options
  const kfzOptions = findOptionsForSelectFilter('kfz', filterConfig);
  const amenityOptions = findOptionsForSelectFilter('amenities', filterConfig);
  const schemaAmenityFeature = currentListing.attributes.publicData.amenities?.map((amenity) => ({
    '@type': 'LocationFeatureSpecification',
    name: amenityOptions.find((option) => option.key === amenity)?.label,
    value: true,
  }));

  const earliestArrivalTimeOptions = findOptionsForSelectFilter(
    'earliestArrivalTime',
    filterConfig
  );
  const latestArrivalTimeOptions = findOptionsForSelectFilter('latestArrivalTime', filterConfig);
  const latestDepartureTimeOptions = findOptionsForSelectFilter(
    'latestDepartureTime',
    filterConfig
  );
  const activitiesOptions = findOptionsForSelectFilter('activities', filterConfig);
  const surroundingsOptions = findOptionsForSelectFilter('surroundings', filterConfig);
  const responseTimeOptions = findOptionsForSelectFilter('responseTime', filterConfig);
  const cancellationPolicyOptions = findOptionsForSelectFilter('cancellationPolicy', filterConfig);

  const amountOfSeats = publicData?.amountOfSeats ? publicData.amountOfSeats : 1;
  const maxGuestCound = publicData.guestOptions?.maxGuestCount || 2;

  const accommodationType = isCampingGroundListing(currentListing) ? 'Campground' : 'CampingPitch';
  const checkinTime = `${earliestArrivalTimeOptions[0].key.replace('_clock', '').padStart(2, '0')}:00`;
  const checkoutTime = `${latestDepartureTimeOptions[latestDepartureTimeOptions.length - 1].key.replace('_clock', '')}:00`;

  return (
    <Page
      author={authorDisplayName}
      contentType="website"
      description={translations.description || description}
      facebookImages={facebookImages}
      schema={{
        '@context': 'https://schema.org',
        '@type': 'VacationRental',
        accommodationType,
        containsPlace: {
          '@type': 'Accommodation',
          amenityFeature: schemaAmenityFeature,
          occupancy: {
            '@type': 'QuantitativeValue',
            value: maxGuestCound,
          },
        },
        address: {
          '@type': 'PostalAddress',
          publicAccess: false,
        },
        description: translations.description || description,
        name: schemaTitle,
        image: schemaImages,
        // Listings can carry no geolocation at all (older drafts saved before
        // the location form gated on valid coordinates). Omit the coordinates
        // from the schema instead of taking the whole page down with it.
        ...(geolocation ? { latitude: geolocation.lat, longitude: geolocation.lng } : {}),
        // Same story as the coordinates: a listing can come back without a price
        // at all. Everything else in this component already reads it defensively
        // (see priceAmount above and the reduced-price block), so the schema is
        // the only place that would take the page down over a missing price.
        ...(price ? { priceRange: priceRange(price.amount) } : {}),
        identifier: currentListing.id.uuid,
        ...(reviews.length > 0
          ? {
              review: schemaReviews,
              aggregateRating: {
                '@type': 'AggregateRating',
                ratingValue: ratingValue,
                bestRating: bestRating,
                worstRating: wortRating,
                ratingCount: ratingCount,
                reviewCount: reviews.length,
              },
            }
          : {}),
        checkinTime,
        checkoutTime,
      }}
      title={schemaTitle}
      twitterImages={twitterImages}
    >
      <LayoutSingleColumn>
        <LayoutWrapperTopbar>{topbar}</LayoutWrapperTopbar>
        <LayoutWrapperMain>
          <Grid
            container
            sx={{
              backgroundColor: 'white',
              mb: isCordova() ? 16 : 0,
            }}
          >
            <Grid size={12}>
              {actionBar}
              <SectionImages isOwnListing={isOwnListing} listing={currentListing} title={title} />
            </Grid>
            <Grid size={12}>
              <SectionHeading
                authorDisplayName={authorDisplayName}
                bookingLimit={bookingLimitValue}
                currentListing={currentListing}
                customLineItemTranslations={translations.customLineItems}
                fetchLineItemsError={fetchLineItemsError}
                fetchLineItemsInProgress={fetchLineItemsInProgress}
                fetchTimeSlotsError={fetchTimeSlotsError}
                fetchTimeSlotsInProgress={fetchTimeSlotsInProgress}
                formattedPrice={formattedPrice}
                geolocation={geolocation}
                getTranslation={getTranslation}
                isOwnListing={isOwnListing}
                isTranslated={isTranslated}
                lineItems={lineItems}
                minimumNights={bookingMinimumValue}
                onFetchTimeSlots={onFetchTimeSlots}
                onFetchTransactionLineItems={onFetchTransactionLineItems}
                onShareListing={onShareListing}
                onSubmit={handleBookingSubmit}
                openShareModal={openShareModal}
                priceTitle={priceTitle}
                reducedFormattedPrice={reducedFormattedPrice}
                reducedPriceTitle={reducedPriceTitle}
                reviewsRef={reviewsRef}
                richTitle={richTitle}
                scrollTargetRef={sectionMoreInformationRef}
                showReducedPrice={showReducedPrice || showTwentyFivePassPrice}
                timeSlots={timeSlots}
                title={bookingTitle}
                toManyTransactionsWithProvider={toManyTransactionsWithProvider}
                unitType={unitType}
                voucher={voucher}
                voucherValidationError={voucherValidationError}
                voucherValidationInProgress={voucherValidationInProgress}
              />
            </Grid>
            <BoxSeparator />
            {!passIsValid ? (
              <Grid size={12}>
                <SectionAnnualPassSavingsMaybe price={price} />
                <BoxSeparator />
              </Grid>
            ) : null}
            {description || translations.description ? (
              <>
                <Grid size={12}>
                  <SectionDescriptionMaybe
                    description={translations.description || description}
                    isTranslated={isTranslated}
                  />
                </Grid>
                <BoxSeparator />
              </>
            ) : null}
            <Grid size={12}>
              <SectionAmenitiesMaybe amenities={publicData?.amenities} options={amenityOptions} />
              <SectionVehiclesMaybe
                allWheelDrive={publicData?.allWheelDrive}
                kfz={publicData?.kfz}
                options={kfzOptions}
              />
            </Grid>

            <BoxSeparator />

            <Grid size={12}>
              <SectionActivitiesMaybe
                activities={publicData?.activities}
                options={activitiesOptions}
              />
              <SectionSurroundingsMaybe
                options={surroundingsOptions}
                surroundings={publicData?.surroundings}
              />
            </Grid>

            <BoxSeparator />

            <Grid ref={sectionMoreInformationRef} size={12}>
              <ListingSection spacing={4}>
                <ListingSectionItem params={{ size: { xs: 12, md: 6, lg: 6 } }}>
                  <SectionMoreInformationMaybe
                    amountOfSeats={amountOfSeats}
                    bookingLimitOptions={bookingLimitOptions}
                    bookingMinimumOption={bookingMinimumOption}
                    publicData={publicData}
                    translations={translations}
                  />
                </ListingSectionItem>
                <ListingSectionItem params={{ size: { xs: 12, md: 6, lg: 6 } }}>
                  <SectionTimingsMaybe
                    earliestArrivalTimeOptions={earliestArrivalTimeOptions}
                    latestArrivalTimeOptions={latestArrivalTimeOptions}
                    latestDepartureTimeOptions={latestDepartureTimeOptions}
                    options={responseTimeOptions}
                    publicData={publicData}
                  />
                </ListingSectionItem>
                <ListingSectionItem params={{ size: { xs: 12, md: 6, lg: 6 } }}>
                  <SectionRulesMaybe
                    isTranslated={isTranslated}
                    rules={translations.rules || publicData.rules}
                  />
                </ListingSectionItem>
                <ListingSectionItem params={{ size: { xs: 12, md: 6, lg: 6 } }}>
                  <SectionCustomLineItemsDescriptionMaybe
                    customLineItems={translations.customLineItems || publicData.customLineItems}
                    isTranslated={isTranslated}
                  />
                </ListingSectionItem>
              </ListingSection>
            </Grid>

            {geolocation && (fetchNearbyInProgress || nearbyListings.length >= NEARBY_MIN) ? (
              <>
                <BoxSeparator />
                <SectionNearbyListingsMaybe
                  inProgress={fetchNearbyInProgress}
                  listings={nearbyListings}
                />
              </>
            ) : null}

            {geolocation ? (
              <>
                <BoxSeparator />
                <SectionMapMaybe
                  geolocation={geolocation}
                  listingId={currentListing.id}
                  location={publicData?.location}
                  publicData={publicData}
                />
              </>
            ) : null}

            {reviews.length > 0 ? (
              <>
                <BoxSeparator />
                <SectionReviews
                  fetchReviewsError={fetchReviewsError}
                  getTranslation={getTranslation}
                  reviews={reviews}
                  reviewsRef={reviewsRef}
                />
              </>
            ) : null}

            {currentListing.author ? (
              <>
                <BoxSeparator />
                <SectionHostMaybe
                  author={currentListing.author}
                  currentUser={currentUser}
                  getTranslation={getTranslation}
                />
              </>
            ) : null}

            <SectionCancellationPolicyMaybe
              cancellationPolicyOptions={cancellationPolicyOptions}
              publicData={publicData}
            />
          </Grid>
        </LayoutWrapperMain>
        <LayoutWrapperFooter>
          <Footer />
        </LayoutWrapperFooter>
        <LayoutWrapperBottombar>
          <BottomNavigationBar />
        </LayoutWrapperBottombar>
      </LayoutSingleColumn>

      <Dialog onClose={() => setNextPeriodDialogOpen(false)} open={nextPeriodDialogOpen}>
        <DialogTitle>
          {intl.formatMessage({
            id: isFlexPass(pass)
              ? 'ListingPage.nextPeriodDialog.flexPass.title'
              : 'ListingPage.nextPeriodDialog.title',
          })}
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            {intl.formatMessage(
              {
                id: isFlexPass(pass)
                  ? 'ListingPage.nextPeriodDialog.flexPass.description'
                  : 'ListingPage.nextPeriodDialog.description',
              },
              {
                validFrom: pass?.rentalStartDate
                  ? intl.formatDate(pass.rentalStartDate, {
                      year: 'numeric',
                      month: 'long',
                      day: '2-digit',
                    })
                  : '',
                validUntil: pass?.validUntil
                  ? intl.formatDate(pass.validUntil, {
                      year: 'numeric',
                      month: 'long',
                      day: '2-digit',
                    })
                  : '',
              }
            )}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleContinueWithoutPass}>
            {intl.formatMessage({
              id: isFlexPass(pass)
                ? 'ListingPage.nextPeriodDialog.flexPass.continue'
                : 'ListingPage.nextPeriodDialog.continueWithoutPass',
            })}
          </Button>
          {!isFlexPass(pass) ? (
            <Button onClick={handleGoToPassSettings} variant="contained">
              {intl.formatMessage({ id: 'ListingPage.nextPeriodDialog.payNextPeriod' })}
            </Button>
          ) : null}
        </DialogActions>
      </Dialog>
    </Page>
  );
};

ListingPage.displayName = 'ListingPage';

export default ListingPage;
